import {useEffect} from 'react'
import {getState, useSelector} from '../../store.js'
import {isSuperuserSelector} from '../../data/me.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import ButtonPrimary from '../../common/components/Button/ButtonPrimary.js'
import apiverson from '../../common/apiverson.js'
import {buildPath} from '../../common/querystring.js'
import {saveCSVFile} from '../../common/createCSVFile.js'
import {loadFileInForm} from '../../common/file.js'
import {showMessageToast} from '../Header/Toast/index.js'
import {showConfirmMessageModal} from '../../common/components/Modal/ConfirmMessageModal.js'
import {createNewTask} from '../../data/tasks.js'
import {FormattedRealDate} from '../../common/components/FormattedDate.js'
import Icon from '../../common/icons/Icon.js'
import {dataImport2Selector, getImportHistory} from './dataImportFunctions.js'
import {formatDateTime} from '../../common/date.js'

async function downloadDocument(document) {
  const {text} = await apiverson.get(
    buildPath(['document', document.id, 'data']),
  )

  saveCSVFile(text, document.document_name)
}

async function showImportTaskManageModal(item) {
  let document

  try {
    const {json} = await apiverson.get(
      buildPath(['document', item.document_id]),
    )

    document = json
  } catch (err) {
    // the task is complete so the document was deleted
  }

  const isSuperUser = isSuperuserSelector(getState())

  await showConfirmMessageModal({
    title: 'Import Details',
    Message({form, updateModalForm, setModalFormValue}) {
      const latestTask = item.tasks[0]

      return (
        <>
          {document && latestTask?.complete && !latestTask.success && (
            <>
              <div className="fs-01 lh-md margin-bottom-25">
                <strong>There are some errors that need to be fixed.</strong>
              </div>
              <ol className="list list--ol">
                <li className="list__item fs-00 lh-md margin-bottom-25">
                  <div className="margin-bottom-5">
                    Download the following CSV that contains only the records
                    with errors.
                  </div>
                  <div>
                    <ButtonPrimary
                      isOutlined
                      size="x-sm"
                      onClick={() => downloadDocument(document)}
                    >
                      Download CSV with Errors
                    </ButtonPrimary>
                  </div>
                </li>
                <li className="list__item fs-00 lh-md margin-bottom-20">
                  Fix the errors in the CSV you just downloaded.
                </li>
                <li className="list__item fs-00 lh-md margin-bottom-0">
                  <div className="margin-bottom-5">
                    Use the following input to re-upload the CSV with the fixed
                    records.
                  </div>
                  <div className="fs-n0 lh-md">
                    <input
                      type="file"
                      name="files[]"
                      id="id_reupload_files"
                      onChange={(event) => {
                        setModalFormValue(
                          ['confirmValue', 'file'],
                          event.target.files[0],
                        )

                        updateModalForm({
                          hideConfirm: false,
                          cancelText: 'Cancel',
                        })
                      }}
                    />
                  </div>
                  {form.confirmValue.file && (
                    <div className="fs-n0 lh-md margin-top-10">
                      <strong>File:</strong> {form.confirmValue.file.name}
                    </div>
                  )}
                </li>
              </ol>
              <div className="divider--top margin-bottom-10">
                <strong className="fs-00 lh-md">More Info:</strong>
              </div>
            </>
          )}
          <ul className="list list--square">
            {item.tasks.map((task) => (
              <li className="list__item margin-bottom-10" key={task.id}>
                {!task.complete && (
                  <div className="fs-00 lh-md">
                    <strong>
                      <em>Import is running...</em>
                    </strong>
                  </div>
                )}
                {task.complete && !task.success && (
                  <div className="fs-00 lh-md">
                    <strong>Import Failed</strong>
                  </div>
                )}
                {task.complete && task.success && (
                  <div className="fs-00 lh-md">
                    <strong>Import Successful</strong>
                  </div>
                )}
                <div className="fs-n0 lh-md">{task.status}</div>

                {isSuperUser && (
                  <div className="fs-n1 lh-md">
                    <a href={task._logurl}>Log</a>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )
    },
    confirmText: 'Restart Upload',
    cancelText: 'Close',
    hideConfirm: true,
    confirmValue: {
      file: null,
    },
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      const formData = loadFileInForm('file', outcome.file)

      await apiverson.put(buildPath(['document', document.id]), formData)

      await createNewTask(item.tasks[0].type, item.tasks[0].params)

      showMessageToast('Restarted Data Import Task')

      getImportHistory()
    },
  })
}

let pollTimeout
function pollForImportTasks() {
  getImportHistory()

  pollTimeout = setTimeout(() => pollForImportTasks(), 30 * 1000)

  return () => {
    clearTimeout(pollTimeout)
  }
}

function Line({item}) {
  const [task] = item.tasks

  if (!task) {
    return null
  }

  return (
    <tr>
      <td className="table__td">
        <strong className="fs-00 lh-md">
          <FormattedRealDate value={task.created} format={formatDateTime} />
        </strong>
      </td>
      <td className="table__td align-center">
        <div className="fs-n0 lh-md">
          {!task.complete && <em>In progress...</em>}
          {task.success ? (
            <Icon name="check" className="fs-02 text--green v-align-middle" />
          ) : task.complete ? (
            <Icon name="close" className="fs-02 error v-align-middle" />
          ) : (
            ''
          )}
        </div>
      </td>
      <td className="table__td">
        <div>
          <strong className="fs-n0 lh-sm-md margin-bottom-5">
            {task.status}
          </strong>
        </div>
        <ButtonLink
          className="fs-n2 lh-md border-underline"
          onClick={() => showImportTaskManageModal(item)}
        >
          More info
        </ButtonLink>
      </td>
    </tr>
  )
}

export default function ImportHistory() {
  const {history} = useSelector(dataImport2Selector)

  useEffect(pollForImportTasks, [])

  return (
    <div className="margin-top-10 margin-bottom-30">
      <div className="wrap--sub-nav wrap--sub-nav-panel">
        <nav className="list--sub-nav">
          <div className="list__item--sub-nav wider">
            <span className="sub-nav__link fs-01 active">
              <span>Import History</span>
            </span>
          </div>
        </nav>
      </div>
      <div className="wrap--table-panel">
        {!history ? (
          <div className="loading align-center margin-top-0 margin-bottom-25">
            <span className="list-processing animate-spin v-align-middle" />
            <strong className="inline-block v-align-middle fs-02 op-75 margin-left-7">
              Loading...
            </strong>
          </div>
        ) : history ? (
          history.length > 0 ? (
            <table className="table fs-00">
              <thead>
                <tr className="fs-00">
                  <th className="table__th w-30">Date</th>
                  <th className="table__th w-30 align-center">Status</th>
                  <th className="table__th w-40">Details</th>
                </tr>
              </thead>
              <tbody className="table__tbody table__tbody--lines">
                {history.map((item) => (
                  <Line key={item.document_id} item={item} />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="align-center margin-top-10">
              <strong className="fs-01 lh-md margin-bottom-0 align-center">
                No imports have been run for this type of data.
              </strong>
            </div>
          )
        ) : null}
      </div>
    </div>
  )
}
